import { type ChangeEvent, useRef, useState } from "react";
import { useTranslation } from "@coworker/locales";
import type { FileUploadConstraints } from "../UploadMediaModal/components/Dropzone/types";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import FixaImage from "@coworker/apprestructured/src/shared/wrappers/FixaImage/FixaImage";
import { FixaInputField } from "@coworker/apprestructured/src/shared/wrappers/FixaInputField/FixaInputField";
import FixaInlineMessage from "@coworker/apprestructured/src/shared/wrappers/FixaInlineMessage/FixaInlineMessage";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import FixaSSRIcon from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/FixaSSRIcon";
import {
  FixaAccordion,
  FixaAccordionItem,
} from "@coworker/apprestructured/src/shared/wrappers/FixaAccordion/FixaAccordion";
import PaperclipIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/PaperclipIconPath";
import TrashCanIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/TrashCanIconPath";
import {
  FixaTab,
  FixaTabPanel,
} from "@coworker/apprestructured/src/shared/wrappers/FixaTabs/FixaTabs";
import FixaTabs from "@coworker/apprestructured/src/shared/wrappers/FixaTabs/FixaTabs";
import FixaTable, {
  FixaTableBody,
  FixaTableHeader,
} from "@coworker/apprestructured/src/shared/wrappers/FixaTable/FixaTable";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import {
  confirmPresentationUpload,
  checkPresentationDuplicate,
  DuplicatePresentationError,
  extractPresentationUpload,
  extractPresentationWithoutArticles,
  RetryWithoutArticlesError,
  type DuplicateAction,
  type MediaLaunch,
  type UploadMedia,
  type UploadWarning,
} from "../../services/uploadPresentationFlow";
import styles from "./launch-media-upload-modal.module.css";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirmed: () => void;
  presentationConstraints: FileUploadConstraints;
  articleConstraints: FileUploadConstraints;
};

type Step = "upload" | "review";

const ImageGroup = ({
  candidate,
  candidateIndex,
  onToggle,
  previews,
  title,
  description,
  testId,
}: {
  candidate: UploadMedia;
  candidateIndex: number;
  onToggle: (candidateIndex: number, previewPath: string) => void;
  previews: { url: string; relative_path: string }[];
  title: string;
  description: string;
  testId: string;
}) => (
  <section className={styles["imageGroup"]} data-testid={testId}>
    <FixaText tagName="h3" textStyle="Heading.S">
      {title} ({previews.length})
    </FixaText>
    <FixaText className={styles["imageGroupDescription"]} tagName="p">
      {description}
    </FixaText>
    {previews.length ? (
      <div className={styles["images"]}>
        {previews.map((preview, index) => (
          <div className={styles["imageCard"]} key={preview.relative_path}>
            <button
              aria-label={`Remove image ${index + 1}`}
              className={styles["imageDeleteButton"]}
              onClick={() => onToggle(candidateIndex, preview.relative_path)}
              type="button"
            >
              <FixaSSRIcon paths={TrashCanIconPath} />
            </button>
            <FixaImage
              alt={`${candidate.name} ${title.toLowerCase()} ${index + 1}`}
              className={styles["image"]}
              src={preview.url}
            />
          </div>
        ))}
      </div>
    ) : (
      <FixaText className={styles["emptyImages"]} tagName="p">
        None extracted.
      </FixaText>
    )}
  </section>
);

const SelectedFile = ({
  file,
  onRemove,
}: {
  file: File;
  onRemove: () => void;
}) => (
  <FixaListView id={`selected-file-${file.name}`} size="small">
    <FixaListViewItem
      control="default"
      controlIcon={null}
      quantityLabel={
        <FixaButton
          htmlType="button"
          iconOnly
          onClick={onRemove}
          size="small"
          ssrIcon={TrashCanIconPath}
        />
      }
      title={file.name}
    />
  </FixaListView>
);

const FileAttachment = ({
  constraints,
  description,
  file,
  id,
  label,
  onFileSelected,
  onRemove,
}: {
  constraints: FileUploadConstraints;
  description?: string;
  file?: File;
  id: string;
  label: string;
  onFileSelected: (file: File) => void;
  onRemove: () => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const accept = constraints.acceptedFileExtensions.join(",");
  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) onFileSelected(file);
    event.target.value = "";
  };

  return (
    <section className={styles["uploadPanel"]}>
      <FixaText tagName="h2" textStyle="Body.M">
        {label}
      </FixaText>
      {description && (
        <FixaText className={styles["uploadDescription"]} tagName="p">
          {description}
        </FixaText>
      )}
      <input
        accept={accept}
        className={styles["hiddenInput"]}
        id={id}
        onChange={onChange}
        ref={inputRef}
        type="file"
      />
      <FixaButton
        htmlType="button"
        iconPosition="trailing"
        onClick={() => inputRef.current?.click()}
        ssrIcon={PaperclipIconPath}
        text="Attach file"
        type="secondary"
      />
      {file && <SelectedFile file={file} onRemove={onRemove} />}
    </section>
  );
};

export const LaunchMediaUploadModal = ({
  isOpen,
  onClose,
  onConfirmed,
  presentationConstraints,
  articleConstraints,
}: Props) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>("upload");
  const [presentation, setPresentation] = useState<File>();
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [isDuplicatePresentation, setIsDuplicatePresentation] = useState(false);
  const [duplicateCheckFailed, setDuplicateCheckFailed] = useState(false);
  const [proceedWithOverride, setProceedWithOverride] = useState(false);
  const [articles, setArticles] = useState<File>();
  const [mediaLaunch, setMediaLaunch] = useState<MediaLaunch>({
    name: "",
  });
  const [candidates, setCandidates] = useState<UploadMedia[]>([]);
  const [warnings, setWarnings] = useState<UploadWarning[]>([]);
  const [presentationReference, setPresentationReference] = useState<{
    bucket: string;
    relative_path: string;
  }>();
  const [articlesReference, setArticlesReference] = useState<{
    bucket: string;
    relative_path: string;
  }>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string>();
  const [duplicate, setDuplicate] = useState<DuplicatePresentationError>();
  const [retryWithoutArticles, setRetryWithoutArticles] = useState<{
    label: string;
    presentation: { bucket: string; relative_path: string };
  }>();
  const requestId = useRef(0);
  const duplicateCheckId = useRef(0);
  const hasValidLaunchInformation = mediaLaunch.name.trim().length > 0;

  const resetAndClose = () => {
    requestId.current += 1;
    duplicateCheckId.current += 1;
    setStep("upload");
    setPresentation(undefined);
    setIsCheckingDuplicate(false);
    setIsDuplicatePresentation(false);
    setDuplicateCheckFailed(false);
    setProceedWithOverride(false);
    setArticles(undefined);
    setMediaLaunch({ name: "" });
    setCandidates([]);
    setWarnings([]);
    setPresentationReference(undefined);
    setArticlesReference(undefined);
    setIsProcessing(false);
    setError(undefined);
    setDuplicate(undefined);
    setRetryWithoutArticles(undefined);
    onClose();
  };

  const selectPresentation = async (file: File) => {
    const currentCheckId = ++duplicateCheckId.current;
    setPresentation(file);
    setIsDuplicatePresentation(false);
    setDuplicateCheckFailed(false);
    setProceedWithOverride(false);
    setIsCheckingDuplicate(true);
    setError(undefined);
    try {
      const duplicate = await checkPresentationDuplicate(file);
      if (currentCheckId === duplicateCheckId.current) {
        setIsDuplicatePresentation(duplicate);
      }
    } catch {
      if (currentCheckId === duplicateCheckId.current) {
        setDuplicateCheckFailed(true);
        setError(
          "We could not check whether this presentation was already processed. Please try again."
        );
      }
    } finally {
      if (currentCheckId === duplicateCheckId.current)
        setIsCheckingDuplicate(false);
    }
  };

  const processFiles = async () => {
    if (!presentation || !articles) return;
    const currentRequestId = ++requestId.current;
    setIsProcessing(true);
    setError(undefined);
    setWarnings([]);
    setDuplicate(undefined);
    setRetryWithoutArticles(undefined);
    try {
      const result = await extractPresentationUpload(presentation, articles);
      if (currentRequestId !== requestId.current) return;
      setCandidates(result.candidates);
      setWarnings(result.warnings);
      setPresentationReference(result.presentation);
      setArticlesReference(result.articles);
      if (result.candidates.length) {
        setStep("review");
      } else {
        setError(
          "No media could be extracted. Choose different files and try again."
        );
      }
    } catch (error) {
      if (currentRequestId !== requestId.current) return;
      if (error instanceof RetryWithoutArticlesError) {
        setError(error.message);
        setRetryWithoutArticles({
          label: error.label,
          presentation: error.presentation,
        });
        return;
      }
      setError(
        error instanceof Error
          ? error.message
          : "We could not process these files. Check the presentation and article workbook, then try again."
      );
    } finally {
      if (currentRequestId === requestId.current) setIsProcessing(false);
    }
  };

  const continueWithoutArticles = async () => {
    if (!retryWithoutArticles) return;
    const currentRequestId = ++requestId.current;
    setIsProcessing(true);
    setError(undefined);
    try {
      const result = await extractPresentationWithoutArticles(
        retryWithoutArticles.presentation
      );
      if (currentRequestId !== requestId.current) return;
      setCandidates(result.candidates);
      setWarnings([]);
      setPresentationReference(result.presentation);
      setArticlesReference(undefined);
      setRetryWithoutArticles(undefined);
      if (result.candidates.length) {
        setStep("review");
      } else {
        setError(
          "No media could be extracted. Choose a different presentation and try again."
        );
      }
    } catch {
      if (currentRequestId !== requestId.current) return;
      setError(
        "We could not process this presentation. Check the file and try again."
      );
    } finally {
      if (currentRequestId === requestId.current) setIsProcessing(false);
    }
  };

  const confirm = async (duplicateAction?: DuplicateAction) => {
    if (!presentationReference || isProcessing) return;
    const currentRequestId = ++requestId.current;
    setIsProcessing(true);
    setError(undefined);
    try {
      await confirmPresentationUpload(
        { presentation: presentationReference, articles: articlesReference },
        candidates,
        { ...mediaLaunch, name: mediaLaunch.name.trim() },
        duplicateAction ?? (proceedWithOverride ? "OVERRIDE" : undefined)
      );
      if (currentRequestId !== requestId.current) return;
      onConfirmed();
      resetAndClose();
    } catch (error) {
      if (currentRequestId !== requestId.current) return;
      if (error instanceof DuplicatePresentationError) {
        setDuplicate(error);
        return;
      }
      setError("We could not save the media. Please try again.");
    } finally {
      if (currentRequestId === requestId.current) setIsProcessing(false);
    }
  };

  const togglePreview = (candidateIndex: number, previewPath: string) => {
    setCandidates((current) =>
      current.map((candidate, index) => {
        if (index !== candidateIndex) return candidate;
        const isIncluded =
          candidate.included_preview_paths.includes(previewPath);
        return {
          ...candidate,
          included_preview_paths: isIncluded
            ? candidate.included_preview_paths.filter(
                (path) => path !== previewPath
              )
            : [...candidate.included_preview_paths, previewPath],
          excluded_preview_paths: isIncluded
            ? [...candidate.excluded_preview_paths, previewPath]
            : candidate.excluded_preview_paths.filter(
                (path) => path !== previewPath
              ),
        };
      })
    );
  };

  const footer = (
    <FixaModalFooter className={styles["footer"]}>
      {step === "upload" && (
        <FixaButton
          disabled={
            !presentation ||
            !articles ||
            !hasValidLaunchInformation ||
            isProcessing ||
            isCheckingDuplicate ||
            duplicateCheckFailed
          }
          htmlType="button"
          loading={isProcessing}
          onClick={() => {
            if (isDuplicatePresentation && !proceedWithOverride) {
              setProceedWithOverride(true);
            }
            processFiles();
          }}
          text={
            isDuplicatePresentation
              ? proceedWithOverride
                ? "Process and override"
                : "Proceed and override"
              : isCheckingDuplicate
                ? "Checking presentation"
                : "Process files"
          }
          type="emphasised"
          fluid
        />
      )}
      {step === "review" && (
        <>
          <FixaButton
            disabled={isProcessing}
            htmlType="button"
            onClick={() => {
              setDuplicate(undefined);
              setError(undefined);
              setStep("upload");
            }}
            text={t("editString")}
            type="secondary"
          />
          {duplicate ? (
            <>
              {(
                duplicate.duplicate.allowed_actions ?? [
                  "CREATE_NEW",
                  "OVERRIDE",
                ]
              ).includes("CREATE_NEW") && (
                <FixaButton
                  disabled={isProcessing}
                  htmlType="button"
                  onClick={() => confirm("CREATE_NEW")}
                  text="Create new media"
                  type="secondary"
                />
              )}
              {(
                duplicate.duplicate.allowed_actions ?? [
                  "CREATE_NEW",
                  "OVERRIDE",
                ]
              ).includes("OVERRIDE") && (
                <FixaButton
                  disabled={isProcessing}
                  htmlType="button"
                  onClick={() => confirm("OVERRIDE")}
                  text="Override existing media"
                  type="emphasised"
                />
              )}
            </>
          ) : (
            <FixaButton
              disabled={isProcessing}
              htmlType="button"
              loading={isProcessing}
              onClick={() => confirm()}
              text={t("confirmString")}
              type="emphasised"
            />
          )}
        </>
      )}
    </FixaModalFooter>
  );

  return (
    <FixaModal
      data-testid="launch-media-upload-modal"
      handleCloseBtn={resetAndClose}
      visible={isOpen}
    >
      <FixaSheets
        ariaLabel="Import media presentation"
        className={styles["sheet"]}
        footer={footer}
        fullSize
        header={
          <FixaModalHeader
            ariaCloseTxt={t("closeActionString")}
            className={styles["header"]}
            closeBtnClick={resetAndClose}
            title="Import media presentation"
          />
        }
      >
        <FixaModalBody className={styles["body"]}>
          <section className={styles["content"]}>
            <FixaText
              id="launch-media-upload-modal-title"
              tagName="h1"
              textStyle="Heading.M"
            >
              {step === "upload" ? "Launch details" : "Confirm media details"}
            </FixaText>
            {error && (
              <div className={styles["error"]} role="alert">
                <FixaInlineMessage
                  variant="negative"
                  body={<FixaText tagName="p">{error}</FixaText>}
                />
                {retryWithoutArticles && (
                  <FixaButton
                    disabled={isProcessing}
                    htmlType="button"
                    onClick={continueWithoutArticles}
                    text={retryWithoutArticles.label}
                    type="secondary"
                  />
                )}
              </div>
            )}
            {step === "upload" && (
              <>
                <section className={styles["launchInformation"]}>
                  <div className={styles["launchFields"]}>
                    <FixaInputField
                      id="launch-name"
                      label="Launch name"
                      onChange={(event) =>
                        setMediaLaunch((current) => ({
                          ...current,
                          name: event.target.value,
                        }))
                      }
                      type="text"
                      value={mediaLaunch.name}
                    />
                  </div>
                </section>
                <section className={styles["launchFields"]}>
                  <FileAttachment
                    constraints={presentationConstraints}
                    file={presentation}
                    id="media-presentation-input"
                    label="Media presentation (.pptx)"
                    onFileSelected={selectPresentation}
                    onRemove={() => {
                      duplicateCheckId.current += 1;
                      setPresentation(undefined);
                      setIsDuplicatePresentation(false);
                      setDuplicateCheckFailed(false);
                      setProceedWithOverride(false);
                      setIsCheckingDuplicate(false);
                    }}
                  />
                  {(isDuplicatePresentation || isCheckingDuplicate) && (
                    <FixaInlineMessage
                      body={
                        isCheckingDuplicate
                          ? "Checking whether this presentation has already been processed."
                          : "Proceeding will process this file again and override the existing media records."
                      }
                      title={
                        isCheckingDuplicate
                          ? "Checking presentation"
                          : "Presentation already processed"
                      }
                      variant={
                        isCheckingDuplicate ? "informative" : "cautionary"
                      }
                    />
                  )}
                </section>
                <section className={styles["launchFields"]}>
                  <FileAttachment
                    constraints={articleConstraints}
                    file={articles}
                    id="article-workbook-input"
                    label="Article list (.xlsx)"
                    onFileSelected={setArticles}
                    onRemove={() => setArticles(undefined)}
                  />
                </section>
              </>
            )}
            {step === "review" && (
              <>
                {duplicate && (
                  <section aria-label="Duplicate presentation" role="alert">
                    <FixaText tagName="h2" textStyle="Heading.S">
                      Media from this presentation already exist
                    </FixaText>
                    <FixaText tagName="p">
                      Create new media for every candidate and keep the earlier
                      media, or override existing media from matching slides.
                      Slides without earlier media will be created as new media.
                      Overriding replaces the files and articles, keeps
                      attribute history, and updates the launch.
                    </FixaText>
                    <ul>
                      {duplicate.duplicate.existing_media.map((media) => (
                        <li key={media.media_id}>
                          <FixaText>
                            {media.name} (slide{" "}
                            {media.source_slide_number ?? "unknown"}
                            {media.launch_id
                              ? `, launch: ${media.launch_id}`
                              : ""}
                            )
                          </FixaText>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
                {warnings.map((warning) => (
                  <FixaInlineMessage
                    body={warning.message}
                    key={`${warning.code}-${warning.message}`}
                    title="Article workbook warning"
                    variant="cautionary"
                  />
                ))}
                <FixaAccordion>
                  {candidates.map((candidate, candidateIndex) => (
                    <FixaAccordionItem
                      id={`media-${candidate.source.slide_number}`}
                      key={`${candidate.source.slide_number}-${candidate.name}`}
                      title={candidate.name}
                    >
                      {(() => {
                        const imagesTabId = `media-${candidate.source.slide_number}-images`;
                        const articlesTabId = `media-${candidate.source.slide_number}-articles`;
                        const attributesId = `media-${candidate.source.slide_number}-attributes`;
                        return (
                          <FixaTabs
                            defaultActiveTab={attributesId}
                            tabs={[
                              <FixaTab
                                key={attributesId}
                                tabPanelId={attributesId}
                                text="Attributes"
                              />,
                              <FixaTab
                                key={imagesTabId}
                                tabPanelId={imagesTabId}
                                text="Images"
                              />,
                              <FixaTab
                                key={articlesTabId}
                                tabPanelId={articlesTabId}
                                text="Articles"
                              />,
                            ]}
                            tabPanels={[
                              <FixaTabPanel
                                key={attributesId}
                                tabPanelId={attributesId}
                              >
                                <FixaAccordion size="small">
                                  {candidate.details.map((detail) => (
                                    <FixaAccordionItem
                                      id={`${attributesId}-${detail.attribute}`}
                                      key={detail.attribute}
                                      title={detail.attribute.replaceAll(
                                        "_",
                                        " "
                                      )}
                                    >
                                      <FixaText tagName="p">
                                        {detail.value}
                                      </FixaText>
                                    </FixaAccordionItem>
                                  ))}
                                </FixaAccordion>
                              </FixaTabPanel>,
                              <FixaTabPanel
                                key={imagesTabId}
                                tabPanelId={imagesTabId}
                              >
                                <div className={styles["imageReview"]}>
                                  <ImageGroup
                                    candidate={candidate}
                                    candidateIndex={candidateIndex}
                                    description="Used as media visuals for this candidate."
                                    onToggle={togglePreview}
                                    previews={(
                                      candidate.preview_images ?? []
                                    ).filter((preview) =>
                                      candidate.included_preview_paths.includes(
                                        preview.relative_path
                                      )
                                    )}
                                    testId={`included-images-${candidate.source.slide_number}`}
                                    title="Included images"
                                  />
                                </div>
                                {!candidate.preview_images?.length &&
                                  candidate.source.image_descriptions.length >
                                    0 && (
                                    <FixaText tagName="p">
                                      Image descriptions:{" "}
                                      {candidate.source.image_descriptions.join(
                                        ", "
                                      )}
                                    </FixaText>
                                  )}
                              </FixaTabPanel>,
                              <FixaTabPanel
                                key={articlesTabId}
                                tabPanelId={articlesTabId}
                              >
                                <FixaTable>
                                  <FixaTableHeader>
                                    <tr>
                                      <th>
                                        <FixaText>Article no.</FixaText>
                                      </th>
                                      <th>
                                        <FixaText>Article name</FixaText>
                                      </th>
                                      <th>
                                        <FixaText>Count</FixaText>
                                      </th>
                                    </tr>
                                  </FixaTableHeader>
                                  <FixaTableBody>
                                    {candidate.articles.map((article) => (
                                      <tr key={article.product_article_id}>
                                        <td>
                                          <FixaText>
                                            {article.product_article_id}
                                          </FixaText>
                                        </td>
                                        <td>
                                          <FixaText>
                                            {article.item_name}
                                          </FixaText>
                                        </td>
                                        <td>
                                          <FixaText>{article.count}</FixaText>
                                        </td>
                                      </tr>
                                    ))}
                                  </FixaTableBody>
                                </FixaTable>
                              </FixaTabPanel>,
                            ]}
                          />
                        );
                      })()}
                    </FixaAccordionItem>
                  ))}
                </FixaAccordion>
              </>
            )}
          </section>
        </FixaModalBody>
      </FixaSheets>
    </FixaModal>
  );
};
